/**
 * Overview strip (W1-15 timeline-host; DESIGN.md §4.5 "Overview strip (right edge of the timeline, 28 px)",
 * ux-landscape §5 B3): a whole-song minimap next to the columns, time flowing up like the timeline.
 *
 * Draws note density (left half) and key density (right half) as per-row histograms (lod.ts `densityBins`,
 * normalised to the densest row), the decorations of every provider over the whole song (markers, BPM changes,
 * loop band, selection, generators, problem ticks with severity shapes), the current viewport as an outlined box
 * and the playhead. Click or drag anywhere seeks there (the host moves the view along; Esc returns the playhead to
 * where it was at the press with a closing 'end' seek, like the gutter's scrub); a drag with the loop
 * modifier (Alt, or Ctrl+Shift in the Alt-free variant; DESIGN.md §5.9 "drag with Alt in the time gutter or
 * overview to set") sets the loop region instead, snapped to the timeline grid, and draws the live band.
 *
 * The strip is not a column: the host places it at the right edge, outside the horizontally scrolling area.
 * Song data comes from `OverviewData` (sorted note and key beats; the timeline panel derives them from the document
 * and pushes them with `host.setOverview`), so the strip never reads the document itself.
 */

import type { Beat } from '../../core/time/types';
import type { TimelineDecoration } from '../types';
import { drawSeverityMark, orderRange, timeRangeF64, type TimeRange } from './gutterColumn';
import { densityBins, normalizeBins } from './lod';
import { severityOfDecoration, type TimelineTheme } from './theme';

export const OVERVIEW_WIDTH = 28;
/** Empty margin at the top and bottom of the strip, CSS px. */
export const OVERVIEW_PAD_PX = 4;
/** Extent shown before any song data arrives (beats). */
const DEFAULT_EXTENT_BEATS = 64;

/** Whole-song data for the minimap. Arrays are sorted ascending (f64 beats). */
export interface OverviewData {
  /** Song start and end in beats (e.g. 0 and the beat of the music's end or the last item). */
  readonly start: number;
  readonly end: number;
  readonly noteBeats: ArrayLike<number>;
  readonly keyBeats?: ArrayLike<number>;
}

export interface OverviewDeps {
  theme(): TimelineTheme;
  data(): OverviewData | null;
  /** Visible beat range of the timeline. */
  viewRange(): readonly [number, number];
  playhead(): number;
  /** Decorations of every provider for a range (called with the whole extent; cached until `invalidate`). */
  decorations(range: readonly [number, number]): readonly TimelineDecoration[];
  onSeek(beat: number, phase: 'start' | 'move' | 'end'): void;
  /**
   * Snap a strip beat for a loop drag; `beatsPerPx` is the strip's resolution (the host snaps to whole beats when
   * the timeline grid is finer than the strip can show).
   */
  snap(beat: number, beatsPerPx: number): Beat;
  /** Loop drag: live / committed / cancelled region (null on 'cancel': keep the old loop). */
  onLoop(range: TimeRange | null, phase: 'live' | 'commit' | 'cancel'): void;
}

/** What a press on the strip does. */
export type OverviewDragMode = 'seek' | 'loop';

export interface OverviewStrip {
  /** Beat range covered by the strip. */
  extent(): readonly [number, number];
  beatAtY(y: number, heightPx: number): number;
  yAtBeat(beat: number, heightPx: number): number;
  draw(ctx: CanvasRenderingContext2D, widthPx: number, heightPx: number): void;
  /**
   * The part that changes only with the song data, the decorations or the size (background, densities, decorations),
   * and the live part drawn over it (viewport box, live loop band, playhead). `draw` = `drawBase` + `drawLive`; the
   * host caches the base while it keeps `baseVersion` (UX-030: on large charts the decorations are thousands of marks).
   */
  drawBase(ctx: CanvasRenderingContext2D, widthPx: number, heightPx: number): void;
  drawLive(ctx: CanvasRenderingContext2D, widthPx: number, heightPx: number): void;
  /** Changes whenever the base drawing may change (`invalidate`). */
  readonly baseVersion: number;
  pointerDown(y: number, heightPx: number, mode?: OverviewDragMode): void;
  pointerMove(y: number, heightPx: number): void;
  pointerUp(y: number, heightPx: number): void;
  /**
   * Ends a drag without committing: a seek drag reports 'end' at the playhead of the press (listeners that commit on
   * 'end' see a closed gesture and the playhead goes back), a loop drag reports 'cancel'.
   */
  cancel(): void;
  /** What the active drag does (null when none). */
  readonly dragMode: OverviewDragMode | null;
  readonly dragging: boolean;
  /** The live loop region of a loop drag (null otherwise or before the drag spans a grid step). */
  readonly liveLoop: TimeRange | null;
  /** Forget cached density bins / decorations (new data, provider change). */
  invalidate(what?: 'data' | 'decorations'): void;
}

/** Strip extent for some data (always non-empty and starting at or before beat 0). */
export function overviewExtent(data: OverviewData | null): readonly [number, number] {
  if (data === null || !Number.isFinite(data.start) || !Number.isFinite(data.end)) return [0, DEFAULT_EXTENT_BEATS];
  const start = Math.min(0, data.start);
  return [start, Math.max(start + 1, data.end)];
}

/** y of a beat in a strip of `heightPx` (time flows up). */
export function overviewYAtBeat(beat: number, extent: readonly [number, number], heightPx: number): number {
  const inner = Math.max(1, heightPx - 2 * OVERVIEW_PAD_PX);
  return heightPx - OVERVIEW_PAD_PX - ((beat - extent[0]) / (extent[1] - extent[0])) * inner;
}

/** Beat at y, clamped to the extent. */
export function overviewBeatAtY(y: number, extent: readonly [number, number], heightPx: number): number {
  const inner = Math.max(1, heightPx - 2 * OVERVIEW_PAD_PX);
  const t = (heightPx - OVERVIEW_PAD_PX - y) / inner;
  const b = extent[0] + Math.min(1, Math.max(0, t)) * (extent[1] - extent[0]);
  return b;
}

export function createOverviewStrip(deps: OverviewDeps): OverviewStrip {
  let drag:
    | { readonly mode: 'seek'; readonly playheadAtPress: number }
    | { readonly mode: 'loop'; readonly startBeat: number; live: TimeRange | null; reported: boolean }
    | null = null;
  let binsCache: {
    data: OverviewData;
    rows: number;
    notes: Float32Array;
    keys: Float32Array | null;
  } | null = null;
  let decCache: { extent: readonly [number, number]; list: readonly TimelineDecoration[] } | null = null;
  let baseVersion = 0;

  const extent = (): readonly [number, number] => overviewExtent(deps.data());

  const bins = (data: OverviewData, rows: number, ext: readonly [number, number]) => {
    if (binsCache !== null && binsCache.data === data && binsCache.rows === rows) return binsCache;
    const notes = normalizeBins(densityBins(data.noteBeats, ext[0], ext[1], rows));
    const keys = data.keyBeats === undefined ? null : normalizeBins(densityBins(data.keyBeats, ext[0], ext[1], rows));
    binsCache = { data, rows, notes, keys };
    return binsCache;
  };

  const decorations = (ext: readonly [number, number]): readonly TimelineDecoration[] => {
    if (decCache !== null && decCache.extent[0] === ext[0] && decCache.extent[1] === ext[1]) return decCache.list;
    decCache = { extent: ext, list: deps.decorations(ext) };
    return decCache.list;
  };

  const seek = (y: number, h: number, phase: 'start' | 'move' | 'end'): void => {
    deps.onSeek(overviewBeatAtY(y, extent(), h), phase);
  };

  const loopRange = (startBeat: number, y: number, h: number): TimeRange | null => {
    const ext = extent();
    const beatsPerPx = (ext[1] - ext[0]) / Math.max(1, h - 2 * OVERVIEW_PAD_PX);
    return orderRange(deps.snap(startBeat, beatsPerPx), deps.snap(overviewBeatAtY(y, ext, h), beatsPerPx));
  };

  const strip: OverviewStrip = {
    extent,
    beatAtY: (y, h) => overviewBeatAtY(y, extent(), h),
    yAtBeat: (b, h) => overviewYAtBeat(b, extent(), h),
    get dragging() {
      return drag !== null;
    },
    get dragMode() {
      return drag?.mode ?? null;
    },
    get liveLoop() {
      return drag?.mode === 'loop' ? drag.live : null;
    },
    get baseVersion() {
      return baseVersion;
    },
    draw(ctx, w, h) {
      strip.drawBase(ctx, w, h);
      strip.drawLive(ctx, w, h);
    },
    drawBase(ctx, w, h) {
      const theme = deps.theme();
      ctx.fillStyle = theme.overviewBackground;
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = theme.divider;
      ctx.fillRect(0, 0, 1, h);
      if (w <= 2 || h <= 2 * OVERVIEW_PAD_PX) return;
      const ext = extent();
      const yOf = (b: number): number => overviewYAtBeat(b, ext, h);
      const data = deps.data();
      const inner = h - 2 * OVERVIEW_PAD_PX;
      const rows = Math.max(1, Math.floor(inner / 2));
      const rowH = inner / rows;
      const half = Math.floor((w - 2) / 2);
      if (data !== null) {
        const b = bins(data, rows, ext);
        ctx.fillStyle = theme.noteDensity;
        for (let i = 0; i < rows; i++) {
          const v = b.notes[i] ?? 0;
          if (v <= 0) continue;
          const y = h - OVERVIEW_PAD_PX - (i + 1) * rowH;
          ctx.fillRect(1, y, Math.max(1, v * (half - 1)), rowH);
        }
        if (b.keys !== null) {
          ctx.fillStyle = theme.keyDensity;
          for (let i = 0; i < rows; i++) {
            const v = b.keys[i] ?? 0;
            if (v <= 0) continue;
            const y = h - OVERVIEW_PAD_PX - (i + 1) * rowH;
            ctx.fillRect(1 + half + 1, y, Math.max(1, v * (half - 1)), rowH);
          }
        }
      }
      for (const d of decorations(ext)) {
        const y = yOf(d.beat);
        const sev = severityOfDecoration(d.kind);
        if (d.endBeat !== undefined && (d.kind === 'loop' || d.kind === 'selection' || d.kind === 'generator')) {
          const y1 = yOf(d.endBeat);
          const top = Math.min(y, y1);
          const hh = Math.max(1, Math.abs(y1 - y));
          if (d.kind === 'loop') {
            ctx.fillStyle = theme.loopFill;
            ctx.fillRect(1, top, w - 1, hh);
            ctx.fillStyle = theme.loop;
            ctx.fillRect(1, top, 2, hh);
          } else if (d.kind === 'selection') {
            ctx.fillStyle = theme.selection;
            ctx.fillRect(1, top, w - 1, hh);
          } else {
            ctx.fillStyle = theme.generator;
            ctx.fillRect(w - 2, top, 2, hh);
          }
        } else if (sev !== null) {
          drawSeverityMark(ctx, sev, w - 4, y, 5, theme.severity[sev]);
        } else if (d.kind === 'marker') {
          ctx.fillStyle = theme.marker;
          ctx.fillRect(1, Math.round(y), w - 1, 1);
        } else if (d.kind === 'bpm') {
          ctx.fillStyle = theme.bpm;
          ctx.fillRect(1, Math.round(y), 5, 2);
        }
      }
    },
    drawLive(ctx, w, h) {
      if (w <= 2 || h <= 2 * OVERVIEW_PAD_PX) return;
      const theme = deps.theme();
      const ext = extent();
      const yOf = (b: number): number => overviewYAtBeat(b, ext, h);
      // Viewport box.
      const [v0, v1] = deps.viewRange();
      const yTop = Math.max(0, yOf(v1));
      const yBottom = Math.min(h, yOf(v0));
      if (yBottom > yTop) {
        ctx.fillStyle = theme.accentMuted;
        ctx.fillRect(1, yTop, w - 1, yBottom - yTop);
        ctx.strokeStyle = theme.accent;
        ctx.lineWidth = 1;
        ctx.strokeRect(1.5, yTop + 0.5, w - 2.5, Math.max(1, yBottom - yTop - 1));
      }
      // Live loop drag on top of everything but the playhead.
      const live = drag?.mode === 'loop' ? drag.live : null;
      if (live !== null) {
        const [s, e] = timeRangeF64(live);
        const yS = yOf(s);
        const yE = yOf(e);
        ctx.fillStyle = theme.loopFill;
        ctx.fillRect(1, yE, w - 1, Math.max(1, yS - yE));
        ctx.fillStyle = theme.loop;
        ctx.fillRect(1, Math.round(yS) - 1, w - 1, 2);
        ctx.fillRect(1, Math.round(yE) - 1, w - 1, 2);
        ctx.fillRect(1, yE, 2, Math.max(1, yS - yE));
      }
      const yP = yOf(deps.playhead());
      if (yP >= 0 && yP <= h) {
        ctx.fillStyle = theme.playhead;
        ctx.fillRect(1, Math.round(yP) - 1, w - 1, 2);
      }
    },
    pointerDown(y, h, mode = 'seek') {
      if (mode === 'loop') {
        drag = { mode: 'loop', startBeat: overviewBeatAtY(y, extent(), h), live: null, reported: false };
        return;
      }
      drag = { mode: 'seek', playheadAtPress: deps.playhead() };
      seek(y, h, 'start');
    },
    pointerMove(y, h) {
      const d = drag;
      if (d === null) return;
      if (d.mode === 'seek') {
        seek(y, h, 'move');
        return;
      }
      const range = loopRange(d.startBeat, y, h);
      if (range === null) {
        // Collapsed back onto the start: hide the band (the drag goes on).
        d.live = null;
        return;
      }
      d.live = range;
      d.reported = true;
      deps.onLoop(range, 'live');
    },
    pointerUp(y, h) {
      const d = drag;
      if (d === null) return;
      drag = null;
      if (d.mode === 'seek') {
        seek(y, h, 'end');
        return;
      }
      // A loop-modifier click without a drag does nothing (like the gutter).
      const range = d.reported ? loopRange(d.startBeat, y, h) : null;
      if (range !== null) deps.onLoop(range, 'commit');
      else if (d.reported) deps.onLoop(null, 'cancel');
    },
    cancel() {
      const d = drag;
      drag = null;
      if (d === null) return;
      // Esc during a seek drag: close the gesture where it started (the press already reported 'start').
      if (d.mode === 'seek') deps.onSeek(d.playheadAtPress, 'end');
      else if (d.reported) deps.onLoop(null, 'cancel');
    },
    invalidate(what) {
      if (what !== 'decorations') binsCache = null;
      if (what !== 'data') decCache = null;
      baseVersion++;
    },
  };
  return strip;
}
